// © 2026 NM Software Development UG (haftungsbeschränkt)
// All rights reserved.

import {Component, inject, OnInit} from '@angular/core';
import {ContractHttpService} from "../x-http-requests/contract-http.service";
import {GlobalAlertService} from "../../../_services/global-alert.service";
import {AlertLevel} from "../../../enums/alert-level";
import {ContractBase, contractCoversDay, contractEndedBefore} from "../x-models/contract-base";
import {CompanyEdto} from "../x-models/company-edto";
import {PageContainerComponent} from "../../../_components/page-container/page-container.component";
import {HeaderContainerComponent} from "../../../_components/header-container/header-container.component";
import {PageScrollableComponent} from "../../../_components/page-scrollable/page-scrollable.component";
import {
  ContractInformationComponent
} from "../../owner/_components/contract-information/contract-information.component";
import {addCalendarMonths, companyToday, DayKey, startOfMonth} from "../../../_shared/time/company-day";
import {CompanyTimeZoneService} from "../../../_shared/time/company-time-zone.service";
import {DayKeyPipe} from "../../../_shared/time/day-key.pipe";

/**
 * **Verträge** — the company's own, read-only view of what its contract licenses and how much of it is in use.
 *
 * The owner screen (`_modules/owner/company-contracts/`) edits contracts; this one only reports, for the
 * company itself. It answers three questions, in that order, and the page is laid out as that order:
 *
 * 1. *What may we do today?* — the running contract's window plus one tile per contracted quantity
 *    (Standorte, Registrierte Geräte, Virtuelle Events), each with the figure actually in use where one
 *    exists.
 * 2. *What changes next?* — the contract that takes over, when a different one does.
 * 3. *What did we have before?* — the ended contracts, as a compact list.
 *
 * Nothing here fetches anything the previous version did not: one `getCurrentCompany()` call carries the
 * contracts and the pair of {@link CompanyEdto.customerValues} (current billing month / next billing month).
 * See `docs/articles/company/contract-customer-values.md` for what those counts mean.
 */
@Component({
  selector: 'app-contracts',
  imports: [
    DayKeyPipe,
    PageContainerComponent,
    HeaderContainerComponent,
    PageScrollableComponent,
    ContractInformationComponent
  ],
  templateUrl: './contracts.component.html',
  styleUrl: './contracts.component.scss'
})
export class ContractsComponent implements OnInit {
  private readonly companyTimeZone = inject(CompanyTimeZoneService);

  constructor(private contractHttpService: ContractHttpService, private gas: GlobalAlertService) {
  }

  ngOnInit(): void {
    this.load();
  }

  /**
   * The 1st of the current billing month in the company's zone, the day `customerValues.item1` is counted for
   * (#802: a day key, compared with the contracts' own days).
   */
  cDay: DayKey = '';
  /** The 1st of the NEXT billing month, the day `customerValues.item2` is counted for. */
  nDay: DayKey = '';

  current: ContractBase | undefined = undefined;
  next: ContractBase | undefined = undefined;

  /** Ended contracts, newest first — computed once per load rather than filtered on every change detection. */
  past: ContractBase[] = [];

  /**
   * Whether the one request has come back (either way).
   *
   * Without it the "no data" branch would be indistinguishable from "the request is still in flight", and the
   * page would flash a failure message at every reader before its own data arrived.
   */
  loaded = false;

  private _company: CompanyEdto | undefined = undefined;

  get company(): CompanyEdto | undefined {
    return this._company;
  }

  set company(value: CompanyEdto | undefined) {
    if (value) {
      // A contract's days are compared with the company's calendar days, both ends included: one ending on the
      // last day of this month is still the current one, and the next takes over on the 1st.
      const today = companyToday(this.companyTimeZone.timeZone());
      this.cDay = startOfMonth(today);
      this.nDay = addCalendarMonths(today, 1);
      this.current = value.contracts.find(x => contractCoversDay(x, this.cDay));
      this.next = value.contracts.find(x => contractCoversDay(x, this.nDay));
      this.past = value.contracts.filter(x => contractEndedBefore(x, this.cDay))
        .sort((a, b) => b.validTo.localeCompare(a.validTo));
    } else {
      this.current = undefined;
      this.next = undefined;
      this.past = [];
    }
    this._company = value;
  }

  load() {
    // Starts the company zone loading; until it answers, "today" is read in the platform default zone.
    this.companyTimeZone.load().subscribe();
    this.contractHttpService.getCurrentCompany()
      .withLoadingIndicator()
      .subscribe({
        next: company => {
          this.company = company;
          this.loaded = true;
        },
        error: err => {
          this.company = undefined;
          this.loaded = true;
          console.error(err);
          this.gas.createAndShow("Fehler", "Vertragsdaten konnten nicht geladen werden.", AlertLevel.error);
        }
      })
  }

  /**
   * The contract that takes over — i.e. `next`, but only while it is a *different* contract than the running
   * one.
   *
   * The usual case is one contract covering both today and the 1st of next month; repeating it under an
   * „Ab …" heading would announce a change that is not one.
   */
  protected get upcoming(): ContractBase | undefined {
    return this.next && this.next.id !== this.current?.id ? this.next : undefined;
  }

  /**
   * Does the device row need its second line? (unchanged condition, kept from the previous version)
   *
   * Keyed on the *usage* figures — the billed device count for next month against this month's — because that
   * is the number that moves for the reader: devices registered in the running month keep billing into the
   * next one, so „ab dem 1." is frequently a different figure even under an unchanged contract.
   */
  protected get deviceUsageChanges(): boolean {
    return this._company?.customerValues.item2?.deviceCount !== this._company?.customerValues.item1?.deviceCount;
  }

  /**
   * Does the parallel-virtual-event allowance change on the 1st of next month? (#664)
   *
   * Only then is the second line worth the space: repeating an unchanged number under "ab 01.11.2026" reads as
   * a change that is not one. `undefined` on either side counts as different from a number, so "no contract
   * next month" (which refuses every event, not just virtual ones) is reported rather than hidden.
   */
  protected get virtualEventCountChanges(): boolean {
    return this.next?.virtualEventCount !== this.current?.virtualEventCount;
  }

  /**
   * A contracted limit as the page prints it: `∞` for the `-1` sentinel, an en dash when there is no contract
   * to read it from.
   *
   * `-1` is this product's documented "unbegrenzt" (see `contract-customer-values.md`), and a row printing a
   * bare `-1` reads as a data error rather than as a licence. Formatting it here rather than in the template
   * keeps the three tiles, the upcoming card and their tests reading one definition.
   */
  protected limitLabel(value: number | undefined): string {
    if (value === undefined || value === null) return '–';
    return value === -1 ? '∞' : `${value}`;
  }

  /** A usage figure as the page prints it — an en dash when the server reported none. */
  protected usageLabel(value: number | undefined): string {
    return value === undefined || value === null ? '–' : `${value}`;
  }

  /**
   * Is a meter meaningful for this limit?
   *
   * Only a finite, positive limit can be filled: `∞` (`-1`) has no end to measure against, and `0` has no
   * width to divide by — both would render as a permanently empty bar that says nothing.
   */
  protected hasMeter(limit: number | undefined): boolean {
    return limit !== undefined && limit > 0;
  }

  /** How full the meter is, capped at 100 % so an over-booked company does not paint past its track. */
  protected meterPct(used: number | undefined, limit: number | undefined): number {
    if (!limit || limit <= 0) return 0;
    return Math.min(100, Math.round(((used ?? 0) / limit) * 100));
  }

  /** True when usage exceeds the contracted limit — the meter turns red AND the tile says so in words. */
  protected meterOver(used: number | undefined, limit: number | undefined): boolean {
    if (limit === undefined || limit === -1) return false;
    return (used ?? 0) > limit;
  }
}
